import React, { useState, useEffect } from 'react'
import { Icon } from '@iconify/react'
import { useNavigate } from 'react-router-dom'
import { getTaskLists, markTaskAsSeen } from '../../../services/user/dashboard.services'
import Loader from '../../../components/common/Loader'
import NoData from '../../../components/common/NoData';
import { encodeData } from '../../../utils/encodeAndEncode'
import { useTimezone } from '../../../context/TimezoneContext';
import { formatUtcInTimeZone } from '../../../utils/timezone';

function TaskList() {
    const { timeZone } = useTimezone();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [tasks, setTasks] = useState([]);
    const user_id = JSON.parse(localStorage.getItem('user'))?.id;
    const [pagination, setPagination] = useState({
        currentPage: 1,
        limit: 20,
        totalTasks: 0,
        hasMore: false
    });

    useEffect(() => {
        fetchTasks();
    }, []);

    const fetchTasks = async () => {
        try {
            setLoading(true);
            const payload = {
                user_id: user_id,
                page: pagination.currentPage,
                limit: pagination.limit
            };
            const response = await getTaskLists(payload);

            if (response.success) {
                setTasks(response.data);
                setPagination(response.pagination);
            }
        } catch (error) {
            // ignore
        } finally {
            setLoading(false);
        }
    };

    const handleMarkAsSeen = async (task) => {
        try {
            const payload = {
                user_id: user_id,
                classroom_id: task.classroom_id,
                task_id: task.task_id,
                [task.task_type === 'assessment' ? 'assessment_id' : 'assignment_id']: task.task_id
            };

            const response = await markTaskAsSeen(payload);
            if (response.success) {
                // Update the local state to mark the task as seen
                setTasks(prevTasks =>
                    prevTasks.map(t =>
                        t.task_id === task.task_id ? { ...t, is_seen: true } : t
                    )
                );
            }
        } catch (error) {
            // ignore
        }
    };

    const formatDateTime = (dateString, format = 'MMM D, h:mm A') => {
        if (!dateString) return null;
        return formatUtcInTimeZone(dateString, timeZone, format);
    };

    const getTaskIcon = (taskType) => {
        switch (taskType) {
            case 'assignment':
                return <Icon icon="fluent:document-text-24-regular" className="!tw-text-[var(--primary-color)] tw-text-brand-600" width="24" height="24" />;
            case 'assessment':
                return <Icon icon="fluent:quiz-new-24-regular" className="!tw-text-[var(--primary-color)] tw-text-brand-600" width="24" height="24" />;
            default:
                return <Icon icon="fluent:task-list-24-regular" className="!tw-text-[var(--primary-color)] tw-text-brand-600" width="24" height="24" />;
        }
    };

    const handleCardClick = async (task) => {
        // First mark the task as seen if it's not already seen
        if (!task.is_seen) {
            await handleMarkAsSeen(task);
        }

        // Then navigate to the classroom details
        const encodedClassroomId = encodeData(task.classroom_id);
        const activeTab = task.task_type === 'assessment' ? 'assessments' : 'assignments';
        navigate(`/user/AllClassroom/classroomDetails/${encodedClassroomId}?active-tab=${activeTab}`);
    };

    return (
        <>

            <div className="ui-card task-list-card tw-flex tw-h-[25rem] tw-min-w-0 tw-flex-col tw-overflow-hidden tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-shadow-sm">
                <div className="ui-card-header tw-flex tw-min-h-12 tw-shrink-0 tw-items-center tw-border-b tw-border-slate-100 tw-bg-white tw-px-4 tw-py-3">
                    <div className="tw-flex tw-justify-between tw-items-center">
                        <h5 className="tw-m-0 tw-flex tw-items-center tw-text-sm tw-font-semibold tw-text-slate-900">
                            <Icon icon="fluent:task-list-square-24-regular" className="tw-me-2 tw-text-brand-600" width={20} height={20} />
                            Task List
                        </h5>
                    </div>
                </div>
                <div className="ui-card-body tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-p-3">
                  <div className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto [scrollbar-width:thin]">
                    {loading ? (
                        <div className="tw-p-6 tw-text-center">
                            <Loader />
                        </div>
                    ) : tasks.length === 0 ? (
                        <div className="tw-p-6">
                            <NoData caption="No tasks available" />
                        </div>
                    ) : (
                        <div className="tw-flex tw-flex-col tw-gap-2">
                            {tasks.map((task) => {
                                const isAssessment = task.task_type === 'assessment';
                                const title = task.title || task.assessment_name || 'Untitled task';
                                const startDate = task.is_time_based ? task.activate_time : task.assigned_date;
                                const endDate = task.is_time_based ? task.deactivate_time : task.due_date;
                                const showDates = (!isAssessment || task.is_time_based) && (startDate || endDate);

                                return (
                                    <button
                                        key={task.task_id}
                                        type="button"
                                        onClick={() => handleCardClick(task)}
                                        aria-label={`Open ${title}`}
                                        className={`tw-flex tw-h-[108px] tw-w-full tw-min-w-0 tw-gap-3 tw-overflow-hidden tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-p-4 tw-text-left tw-shadow-sm tw-transition-colors hover:tw-border-brand-300 hover:tw-bg-slate-50 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-brand-600 ${!task.is_seen ? 'tw-border-l-4 tw-border-l-brand-600 tw-bg-blue-50' : ''}`}
                                    >
                                        <span className="tw-flex tw-size-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-bg-brand-50">
                                            {getTaskIcon(task.task_type)}
                                        </span>
                                        <span className={`tw-flex tw-min-w-0 tw-flex-1 tw-flex-col ${showDates ? 'tw-justify-between' : 'tw-justify-center tw-gap-3'}`}>
                                            <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-2">
                                                <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-sm tw-font-semibold tw-text-slate-900" title={title}>{title}</span>
                                                {!task.is_seen && <span className="tw-shrink-0 tw-rounded-full tw-bg-brand-600 tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-text-white">New</span>}
                                            </span>
                                            <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-2">
                                                <span className="tw-inline-flex tw-shrink-0 tw-items-center tw-gap-1 tw-rounded-full tw-bg-brand-50 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-medium tw-text-brand-700">
                                                    {isAssessment ? 'Assessment' : 'Assignment'}
                                                </span>
                                                {isAssessment && <span className="tw-min-w-0 tw-truncate tw-text-[11px] tw-text-slate-500">{task.is_time_based ? 'Timed' : 'No time limit'}</span>}
                                            </span>
                                            {showDates && <span className="tw-grid tw-min-w-0 tw-grid-cols-2 tw-gap-2 tw-text-[11px] tw-text-slate-500">
                                                <span className="tw-min-w-0 tw-truncate" title={startDate ? `Start: ${formatDateTime(startDate, 'MMM D YYYY hh:mm A')}` : undefined}>
                                                    {startDate ? `Start: ${formatDateTime(startDate)}` : 'Start: —'}
                                                </span>
                                                <span className="tw-min-w-0 tw-truncate" title={endDate ? `Due: ${formatDateTime(endDate, 'MMM D YYYY hh:mm A')}` : undefined}>
                                                    {endDate ? `Due: ${formatDateTime(endDate)}` : 'Due: —'}
                                                </span>
                                            </span>}
                                        </span>
                                    </button>
                                );
                            })}
                            {pagination.hasMore && (
                                <div className="tw-min-w-[0px] ui-column tw-w-full tw-text-center tw-mb-2">
                                    <button
                                        className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[1px] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [[data-theme='dark']_&]:tw-border-[#4895ef] [[data-theme='dark']_&]:tw-text-[#4895ef] [[data-theme='dark']_&:hover]:tw-bg-[#4895ef] [[data-theme='dark']_&:hover]:tw-text-[white] tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] tw-border-[rgb(199_210_254_/_var(--tw-border-opacity,_1))] tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-border-opacity:1] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] [&:hover]:tw-text-[white] [&:hover]:tw-bg-[#4169E1] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] tw-rounded-[0.375rem] tw-pl-3 tw-pr-3 tw-pt-1.5 tw-pb-1.5 tw-text-[0.75rem] tw-leading-[1rem] ui-button ui-button-outline-primary ui-button-sm"}
                                        onClick={() => {
                                            setPagination(prev => ({
                                                ...prev,
                                                currentPage: prev.currentPage + 1
                                            }));
                                            fetchTasks();
                                        }}
                                    >
                                        <Icon icon="fluent:arrow-sync-24-regular" className="tw-me-1" />
                                        Load More Tasks
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                  </div>
                </div>
            </div>
        </>
    )
}

export default TaskList
